import { useInfiniteScroll } from '@heroui/use-infinite-scroll';
import { Key, useEffect, useState } from 'react';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useAsyncSearchableList } from './useAsyncSearchableList';
import { PaginatedBody, SearchQuery, SortOrderQuery } from '@/api/types';
import { SelectItemType } from '@/types';

type UseInfiniteDropdownType<T, R, F> = {
  setValue: (value: string) => void;
  listQuery: (
    filters: SearchQuery & Partial<F>,
  ) => UseInfiniteQueryResult<InfiniteData<PaginatedBody<T>, unknown>, Error>;
  mapper?: (item: T) => R;
  initialName?: string;
  clearOnSearch?: boolean;
  defaultSortOrder?: SortOrderQuery;
  defaultFilters?: F;
};

export const useInfiniteDropdown = <
  T extends Record<string, unknown>,
  R extends SelectItemType,
  F extends Record<string, unknown> = Record<string, unknown>,
>({
  setValue,
  listQuery,
  mapper,
  initialName,
  clearOnSearch = true,
  defaultSortOrder = {},
  defaultFilters = {} as F,
}: UseInfiniteDropdownType<T, R, F>) => {
  const [selectedItemName, setSelectedItemName] = useState<string>(initialName || '');
  const [isAutocompleteOpen, setAutocompleteOpen] = useState(false);
  const { list, isLoading, hasNextPage, loadNextPage, searchValue, setSearchValue } = useAsyncSearchableList({
    query: listQuery,
    mapper,
    defaultSortOrder,
    defaultFilters,
  });

  useEffect(() => {
    if (initialName) {
      setSelectedItemName(initialName);
      setSearchValue(initialName);
    }
  }, [initialName, setSearchValue]);

  const [, itemsScrollerRef] = useInfiniteScroll({
    hasMore: hasNextPage,
    isEnabled: isAutocompleteOpen,
    shouldUseLoader: false,
    onLoadMore: () => loadNextPage(),
  });

  const onItemSelectChange = (key: Key | null) => {
    if (!key) {
      setValue('');
      setSelectedItemName('');
      setSearchValue('');
      return;
    }

    const currentItem = list.find((h) => h.value === key.toString());

    if (currentItem) {
      setValue(currentItem.value.toString());
      setSelectedItemName(currentItem.name as string);
      setSearchValue(currentItem.name);
    }
  };

  const onSearchValue = (text: string) => {
    if (clearOnSearch) {
      setValue('');
      setSelectedItemName('');
    }
    setSearchValue(text);
  };

  return {
    selectedItemName,
    list,
    isLoading,
    searchValue,
    setSearchValue,
    setAutocompleteOpen,
    itemsScrollerRef,
    onItemSelectChange,
    onSearchValue,
  };
};
